// KnowledgeHealthPanel.js - Memory strength dashboard component
// Shows knowledge decay status for all topics based on spaced repetition science

import React, { useEffect } from 'react';
import { isDemoMode } from '../config';

const KnowledgeHealthPanel = ({ 
  knowledgeHealth, 
  masteredTopics, 
  setMasteredTopics,
  setShowKnowledgeHealth,
  setMode,
  setPrompt
}) => {
  // Helper to normalize subject names for comparison
  const normalizeSubject = (s) => s ? s.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '';

  // Lock background scroll when panel is open
  useEffect(() => {
    const mainContent = document.querySelector('.main-content');
    const scrollY = window.scrollY;
    const mainScrollTop = mainContent ? mainContent.scrollTop : 0;
    
    // Lock scrolling on background elements
    document.body.style.overflow = 'hidden';
    if (mainContent) mainContent.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = '';
      if (mainContent) {
        mainContent.style.overflow = '';
        mainContent.scrollTop = mainScrollTop;
      }
      window.scrollTo(0, scrollY);
    };
  }, []);

  // Handler to stop scroll from leaking to background
  const handleWheel = (e) => {
    e.stopPropagation();
  };

  // Demo mode: all hooks have run, safe to return early
  if (isDemoMode()) {
    return (
      <div
        onWheel={(e) => e.stopPropagation()}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)', zIndex: 1000,
          display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
          paddingTop: '40px', overflowY: 'auto'
        }}>
        <div style={{
          backgroundColor: '#1f2937', borderRadius: '16px',
          border: '2px solid rgba(210, 153, 34, 0.3)',
          padding: '32px', maxWidth: '800px', width: '90%',
          maxHeight: '85vh', overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <div>
              <h2 style={{ margin: 0, color: '#fff', fontSize: '24px' }}>🧠 Knowledge Health</h2>
              <p style={{ margin: '4px 0 0 0', color: '#9ca3af', fontSize: '14px' }}>Memory strength based on spaced repetition science</p>
            </div>
            <button onClick={() => setShowKnowledgeHealth(false)}
              style={{ padding: '8px 16px', backgroundColor: '#374151', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
              onMouseOver={(e) => { e.target.style.backgroundColor = '#f85149'; }}
              onMouseOut={(e) => { e.target.style.backgroundColor = '#374151'; }}
            >✕</button>
          </div>
          <div style={{ color: '#fffacd', whiteSpace: 'pre-wrap', lineHeight: '1.8', fontSize: '14px' }}>
            {`🔒 Knowledge Health — Locked in Demo Mode

With a real account this panel tracks how well you're retaining each Python
topic over time using spaced repetition science (similar to Anki or Duolingo).

For every topic you've quizzed on, you see:

  🧠  Memory Strength %   — how strong your recall is right now (decays over time)
  📅  Days Since Review   — how long ago you last answered a question on this topic
  🚦  Urgency status:
        🔴  Review NOW   — memory has decayed significantly, needs immediate attention
        🟡  Review Soon  — starting to fade, worth revisiting this week
        🔵  Upcoming     — still fresh but schedule a review soon
        🟢  Healthy      — strong recall, no action needed yet

Example view after 2 weeks of studying:

  Topic              Memory Strength  Days Since  Status
  ───────────────────────────────────────────────────────
  Variables               92%            1 day    🟢 Healthy
  For Loops               58%            6 days   🟡 Review Soon
  Functions               34%           12 days   🔴 Review NOW
  File I/O                81%            3 days   🔵 Upcoming

Clicking a topic sends a targeted review question to the tutor so you
can reinforce it before the memory fades completely.

✨ Sign up to start tracking your Knowledge Health!`}
          </div>
        </div>
      </div>
    );
  }

  // Summary Stats
  const subjects = Object.values(knowledgeHealth);
  const critical = subjects.filter(s => s.urgency === 'critical').length;
  const soon = subjects.filter(s => s.urgency === 'soon').length;
  const upcoming = subjects.filter(s => s.urgency === 'upcoming').length;
  const healthy = subjects.filter(s => s.urgency === 'healthy').length;
  const avgStrength = subjects.length > 0 
    ? Math.round(subjects.reduce((sum, s) => sum + s.memoryStrength, 0) / subjects.length) 
    : 0;

  return (
    <div 
      onWheel={handleWheel}
      onTouchMove={handleWheel}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        zIndex: 1000,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        paddingTop: '40px',
        overflowY: 'auto'
      }}>
      <div style={{
        backgroundColor: '#1f2937',
        borderRadius: '16px',
        border: '2px solid rgba(210, 153, 34, 0.3)',
        padding: '24px',
        maxWidth: '800px',
        width: '90%',
        maxHeight: '85vh',
        overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2 style={{ margin: 0, color: '#fff', fontSize: '24px' }}>🧠 Knowledge Health</h2>
            <p style={{ margin: '4px 0 0 0', color: '#9ca3af', fontSize: '14px' }}>
              Memory strength based on spaced repetition science
            </p>
          </div>
          <button
            onClick={() => setShowKnowledgeHealth(false)}
            style={{
              padding: '8px 16px',
              backgroundColor: '#374151',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
            onMouseOver={(e) => { e.target.style.backgroundColor = '#f85149'; e.target.style.color = '#ffffff'; }}
            onMouseOut={(e) => { e.target.style.backgroundColor = '#374151'; e.target.style.color = '#fff'; }}
          >
            ✕
          </button>
        </div>

        {/* Summary Stats */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <div style={{ 
            flex: 1, 
            minWidth: '150px',
            padding: '16px', 
            backgroundColor: '#111827', 
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '36px', fontWeight: 'bold', color: avgStrength >= 70 ? '#22c55e' : avgStrength >= 50 ? '#eab308' : '#ef4444' }}>
              {avgStrength}%
            </div>
            <div style={{ fontSize: '14px', color: '#9ca3af' }}>Avg Memory Strength</div>
          </div>
          <div style={{ 
            flex: 1, 
            minWidth: '120px',
            padding: '16px', 
            backgroundColor: '#ef444420', 
            borderRadius: '12px',
            textAlign: 'center',
            border: critical > 0 ? '2px solid #ef4444' : 'none'
          }}>
            <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#ef4444' }}>{critical}</div>
            <div style={{ fontSize: '14px', color: '#ef4444' }}>Need Review NOW</div>
          </div>
          <div style={{ 
            flex: 1, 
            minWidth: '120px',
            padding: '16px', 
            backgroundColor: '#eab30820', 
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#eab308' }}>{soon}</div>
            <div style={{ fontSize: '14px', color: '#eab308' }}>Review Soon</div>
          </div>
          <div style={{ 
            flex: 1, 
            minWidth: '120px',
            padding: '16px', 
            backgroundColor: '#3b82f620', 
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#3b82f6' }}>{upcoming}</div>
            <div style={{ fontSize: '14px', color: '#3b82f6' }}>Upcoming</div>
          </div>
          <div style={{ 
            flex: 1, 
            minWidth: '120px',
            padding: '16px', 
            backgroundColor: '#22c55e20', 
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#22c55e' }}>{healthy}</div>
            <div style={{ fontSize: '14px', color: '#22c55e' }}>Healthy</div>
          </div>
        </div>

        {/* Subject List */}
        <div style={{ marginBottom: '16px' }}>
          <h3 style={{ color: '#fff', margin: '0 0 12px 0', fontSize: '16px' }}>Topics by Memory Strength</h3>
        </div>

        {Object.keys(knowledgeHealth).length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📚</div>
            <div>No practice history yet. Start practicing to see your knowledge health!</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {Object.values(knowledgeHealth)
              .sort((a, b) => a.memoryStrength - b.memoryStrength) // Show weakest first
              .map((item, idx) => {
                // Check if this topic is marked as mastered
                const normalizedSubject = normalizeSubject(item.subject);
                const isMastered = masteredTopics.includes(normalizedSubject);
                
                // Toggle mastered status
                const toggleMastered = () => {
                  const username = localStorage.getItem('username') || 'default';
                  let updated;
                  if (isMastered) {
                    updated = masteredTopics.filter(t => t !== normalizedSubject);
                  } else {
                    updated = [...masteredTopics, normalizedSubject];
                  }
                  setMasteredTopics(updated);
                  localStorage.setItem(`masteredTopics_${username}`, JSON.stringify(updated));
                };
                
                const urgencyColors = {
                  critical: { bg: '#ef444420', border: '#ef4444', text: '#ef4444', icon: '🔴' },
                  soon: { bg: '#eab30820', border: '#eab308', text: '#eab308', icon: '🟡' },
                  upcoming: { bg: '#3b82f620', border: '#3b82f6', text: '#3b82f6', icon: '🔵' },
                  healthy: { bg: '#22c55e20', border: '#22c55e', text: '#22c55e', icon: '🟢' }
                };
                // If mastered, use gray colors
                const masteredColors = { bg: '#37415120', border: '#6b7280', text: '#6b7280', icon: '✓' };
                const colors = isMastered ? masteredColors : (urgencyColors[item.urgency] || urgencyColors.healthy);

                return (
                  <div 
                    key={idx}
                    style={{
                      padding: '16px',
                      backgroundColor: colors.bg,
                      borderRadius: '12px',
                      borderLeft: `4px solid ${colors.border}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '12px'
                    }}
                  >
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span>{colors.icon}</span>
                        <span style={{ color: isMastered ? '#6b7280' : '#fff', fontWeight: 'bold', fontSize: '16px', textDecoration: isMastered ? 'line-through' : 'none' }}>{item.subject}</span>
                        <button
                          onClick={toggleMastered}
                          title={isMastered ? 'Unmark as mastered' : 'Mark as mastered (I know this well)'}
                          style={{
                            padding: '2px 8px',
                            backgroundColor: isMastered ? '#22c55e' : 'transparent',
                            color: isMastered ? '#fff' : '#6b7280',
                            border: isMastered ? 'none' : '1px solid #6b7280',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            marginLeft: '4px'
                          }}
                        >
                          {isMastered ? '✓ Mastered' : '✓'}
                        </button>
                      </div>
                      <div style={{ color: '#9ca3af', fontSize: '13px' }}>
                        Last practiced: {item.lastPracticeDate || 'Unknown'} ({item.daysSinceReview} days ago)
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {/* Memory Strength Bar */}
                      <div style={{ width: '120px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ color: '#9ca3af', fontSize: '11px' }}>Memory</span>
                          <span style={{ color: colors.text, fontSize: '11px', fontWeight: 'bold' }}>{item.memoryStrength}%</span>
                        </div>
                        <div style={{ 
                          height: '8px', 
                          backgroundColor: '#374151', 
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            height: '100%',
                            width: `${item.memoryStrength}%`,
                            backgroundColor: colors.border,
                            borderRadius: '4px',
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                      </div>

                      {/* Accuracy Badge */}
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#9ca3af', fontSize: '11px' }}>Accuracy</div>
                        <div style={{ color: '#fff', fontWeight: 'bold' }}>{item.accuracy}%</div>
                      </div>

                      {/* Reviews Count */}
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#9ca3af', fontSize: '11px' }}>Reviews</div>
                        <div style={{ color: '#fff', fontWeight: 'bold' }}>{item.reviewCount}</div>
                      </div>

                      {/* Action Button - always show, but style based on urgency */}
                      <button
                        onClick={() => {
                          setShowKnowledgeHealth(false);
                          setMode('building-blocks');
                          setPrompt(`I want to practice ${item.subject}`);
                        }}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: item.needsReview ? colors.border : 'transparent',
                          color: item.needsReview ? '#fff' : colors.text,
                          border: item.needsReview ? 'none' : `1px solid ${colors.border}`,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {item.urgency === 'critical' ? 'Review Now' : 
                         item.urgency === 'soon' ? 'Review Soon' : 'Practice'}
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        )}

        {/* Info Footer */}
        <div style={{ 
          marginTop: '24px', 
          padding: '16px', 
          backgroundColor: '#111827', 
          borderRadius: '12px',
          fontSize: '13px',
          color: '#9ca3af'
        }}>
          <strong style={{ color: '#fff' }}>💡 How Memory Strength Works:</strong>
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
            <li>Memory naturally decays over time (Ebbinghaus forgetting curve)</li>
            <li>More practice sessions = slower decay rate</li>
            <li>Higher accuracy = stronger initial memory encoding</li>
            <li>Review topics when they drop below 60% for optimal retention</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeHealthPanel;
